import { FormEvent, FunctionComponent, useEffect, useState } from "react"
import { useSearchParams } from "react-router-dom"
import {
  GetUserapiAdminTestsGetOrganizationTestByOrganizationIdApiResponse,
  useGetUserapiAdminTestsGetOrganizationTestByOrganizationIdQuery,
  usePostUserapiUsersDataLoginMutation,
  usePostUserapiOrgCheckOrgMutation,
  usePostUserapiAdminTestsGetRunIdMutation,
} from "../../../app/services/meseeknaApi"
import {
  RootState,
  useTypedDispatch,
  useTypedSelector,
} from "../../../app/store"
import { loginUser } from "../authThunks"
import { User } from "../types"

import LoginInput from "./login-input"
import NotificationContainer from "./Notifications/NotificationContainer"
import BaseButton from "../../../components/Button/base-button"
import { setRole } from "../authSlice"
import { convertAndUpdateOrganization } from "../authSlice"
import { useRoleRedirect } from "../hooks/useRoleRedirect"

type LoginFieldType = {
  id: string
  title: string
  placeholder: string
  type: string
}

export type AuthenticationHandler = (params: {
  email: string
  password: string
}) => Promise<{
  code: number
  status: string
  data?: User[]
  message?: string
}>

const LoginForm: FunctionComponent<{
  onAuthenticate?: AuthenticationHandler
}> = ({ onAuthenticate }) => {
  const dispatch = useTypedDispatch()
  const {
    getSimulationStatus,
    handleSimulationStatusRedirect,
    handleRoleRedirectForUser,
  } = useRoleRedirect()

  // Objects from persisted state
  const authStore = useTypedSelector((state: RootState) => {
    return state.authSlice
  })

  const [orgFields, setOrgFields] = useState<LoginFieldType[]>([])
  const [loginErrors, setLoginErrors] = useState<string[]>([])
  const [errorMessage, setErrorMessage] = useState<string>("")
  const [runId, setRunId] = useState<number>(0)

  // API calls
  const [userLogin] = usePostUserapiUsersDataLoginMutation()
  const [checkOrg] = usePostUserapiOrgCheckOrgMutation()
  const { data: orgTestData, refetch: getOrganizationTest } =
    useGetUserapiAdminTestsGetOrganizationTestByOrganizationIdQuery(
      { organizationId: authStore.user?.organizationId || 0 },
      { skip: !authStore.user?.organizationId },
      // {
      //   organizationId: Number(authStore.organization.id),
      // },
      // {
      //   skip:
      //     waitingForUser || (authStore.role !== "5" && authStore.role !== "4"),
      // },
    )

  const [getRunId] = usePostUserapiAdminTestsGetRunIdMutation()

  const [searchParams] = useSearchParams()

  const handleLogin = async (fields: { [key: string]: string }) => {
    setLoginErrors([])
    // TODO: This will normally be using email/password BUT we need
    // the ability to use other methods (SSO, 3rd party, etc.) - On hold
    // so we need to pass in the fields from the organization
    // and use those instead of email/password.
    // For now, we are just using email/password.
    const email = fields.email
    const password = fields.password

    try {
      // Use custom authentication handler if provided, otherwise use default
      const authResult = onAuthenticate
        ? await onAuthenticate({ email, password })
        : await userLogin({
            body: { email, password },
          }).unwrap()

      if (authResult.code === 200 && authResult.status === "success") {
        if ("data" in authResult && authResult.data) {
          console.debug("✅  Logged in ok!")
          const userData = authResult.data[0] as User
          console.log("userData", userData)
          const loggedInSentinel = document.querySelector(
            "[data-testid=logged-in]",
          )
          if (loggedInSentinel) {
            loggedInSentinel.textContent = "true"
          }

          console.log("User login data:", {
            id: userData.id,
            organizationId: userData.organizationId,
            organizationName: userData.organizationName,
          })

          dispatch(loginUser(userData))

          // Get organization for user
          const orgResult = await checkOrg({
            body: { organizationName: userData.organizationName },
          }).unwrap()

          if (orgResult.code === 200) {
            // Ensure the organization ID matches the one from the user data
            const updatedOrgResult = {
              ...orgResult,
              data: {
                ...orgResult.data,
                id: userData.organizationId, // Use organizationId from user data
              },
            }
            dispatch(convertAndUpdateOrganization(updatedOrgResult))
            dispatch(
              setRole({ roleId: userData.role, name: userData.roleName }),
            )

            // Log organization data for verification
            console.log("Organization data updated:", {
              originalOrgId: orgResult.data?.id,
              userOrgId: userData.organizationId,
              updatedOrgId: updatedOrgResult.data?.id,
            })
          } else {
            setErrorMessage(orgResult.message ?? "")
          }
          //checking test assignment for candidate and team member
          if (
            (String(userData.role) === "5" || String(userData.role) === "4") &&
            userData.organizationId
          ) {
            const orgTestResponse = await getOrganizationTest()
            if (
              orgTestResponse?.data?.data &&
              orgTestResponse.data.data.length > 0
            ) {
              const testData = orgTestResponse.data.data[0]
              if (testData == null || testData.runId == null) {
                setErrorMessage("Test is not assigned please contact support")
                console.debug("Test is not assigned please contact support")
              } else if (
                testData.testId &&
                userData.organizationId &&
                userData.id
              ) {
                await checkSimulationStatus(
                  testData.testId,
                  userData.organizationId,
                  userData.id,
                  // runId ?? 0
                )
              }
            }
          } else if (userData.role) {
            handleRoleRedirectForUser(userData)
          } else {
            console.error("Error logging in:", authResult)
            setLoginErrors([
              "Unexpected error occurred. Please try again later.",
            ])
          }
        } else {
          console.error("Error logging in:", authResult)
          setLoginErrors(["Unexpected error occurred. Please try again later."])
        }
      } else {
        if (authResult.message) {
          setLoginErrors([authResult.message])
        }
      }
    } catch (error) {
      console.error("Error checking simulation status:", error)
      const errorObject = error as { data?: { message?: string } }
      const errorMessage = errorObject?.data?.message || "Something went wrong"
      setErrorMessage(errorMessage)
    }
  }

  async function checkSimulationStatus(
    testId: number,
    orgId: number,
    userId: number,
    // runId: number,
  ) {
    try {
      const testStatusEl = document.querySelector(
        "[data-testid=test-complete-status]",
      )

      const simStatusRes = await getSimulationStatus(testId, orgId, userId)
      // const microLearningsDataStatus =
      //   (microLearningsData &&
      //     microLearningsData.data &&
      //     microLearningsData.data.length > 0) ? true : false
      switch (simStatusRes.status) {
        case "in-progress":
          console.debug("✅  Simulation is in progress!")
          if (testStatusEl) {
            testStatusEl.textContent = "in-progress"
          }
          break
        case "not-started":
          console.debug("✅  Simulation is not started!")
          if (testStatusEl) {
            testStatusEl.textContent = "not-started"
          }
          break
        case "completed":
          console.debug("✅  Simulation is completed!")
          if (testStatusEl) {
            testStatusEl.textContent = "completed"
          }

          break
      }

      handleSimulationStatusRedirect(simStatusRes.status)
    } catch (error) {
      const errorObject = error as { data?: { message?: string } }
      const errorMessage = errorObject?.data?.message || "Something went wrong"
      setErrorMessage(errorMessage)
      console.error("Error checking simulation status:", error)
    }
  }

  const getOrganizationFieldValues = () => {
    const fields: { [key: string]: string } = {}
    orgFields.forEach((field) => {
      const element = document.getElementById(field.id) as HTMLInputElement
      if (element) {
        fields[field.id] = element.value
      }
    })
    return fields
  }

  const handleSignInClick = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const fields = getOrganizationFieldValues()
    // Remove dependency on authStore for initial login
    handleLogin(fields)
  }

  const getRunIdResponse = async (
    testId: number,
    orgId: number,
    userId: number,
  ) => {
    const response = getRunId({
      testId,
      organizationId: orgId,
      userId: userId,
    }).unwrap()
    return response
  }

  useEffect(() => {
    if (!authStore.user) {
      console.error("Trying to get org test data but no user logged in")
      return
    }
    const handleOrgTestData = async () => {
      const response:
        | GetUserapiAdminTestsGetOrganizationTestByOrganizationIdApiResponse
        | undefined = orgTestData
      const { data: responseData = [] } = response || {}

      if (
        responseData &&
        "data" in responseData &&
        Array.isArray(responseData.data) &&
        responseData.data.length > 0 &&
        (responseData.data[0] == null || responseData.data[0].runId == null)
      ) {
        setErrorMessage("Test is not assigned please contact support")
        console.debug("Test is not assigned please contact support")
      } else {
        const { testId, organizationId: orgId } = responseData[0] || {}
        const userId = authStore.user?.id
        console.log("testId / orgId / userId", testId, orgId, userId)
        if (testId && orgId && userId) {
          const response = await getRunIdResponse(testId, orgId, userId)
          console.log("runId response", response)
          if (Array.isArray(response) && response.length > 0) {
            // const runId = response[0].id
            setRunId(runId)
            checkSimulationStatus(testId, orgId, Number(userId))
          }
          if (runId === 0) {
            checkSimulationStatus(testId, orgId, Number(userId))
          }
        }
      }
    }
    handleOrgTestData()
  }, [orgTestData])

  useEffect(() => {
    let orgFields = authStore.organization?.fields
    if (!orgFields) {
      orgFields = [
        {
          id: "email",
          title: "Account",
          placeholder: "Enter your account name",
          type: "text",
        },
        {
          id: "password",
          title: "Password",
          placeholder: "Enter your password",
          type: "password",
        },
      ]
    }
    setOrgFields(orgFields)
  }, [])

  useEffect(() => {
    const email = searchParams.get("m")
    const otp = searchParams.get("l")

    if (email && otp) {
      // Automatically attempt login with OTP
      handleLogin({
        email: email,
        password: otp,
      })
    }
  }, [searchParams])

  return (
    <div className="self-stretch flex flex-col items-start justify-start gap-[20px]">
      <NotificationContainer errorMessage={errorMessage} successMessage={""} />
      <div className="hidden" data-testid="logged-in" />
      <div className="hidden" data-testid="test-complete-status" />
      {(authStore.organization?.headMessage ||
        authStore.organization?.bodyMessage) && (
        <div className="self-stretch relative leading-[24px] p-4 text-sm bg-lightblue">
          <div className="relative leading-[24px] text-base font-bold">
            {authStore.organization?.headMessage || <div></div>}
          </div>
          <div className="relative leading-[24px] text-sm">
            {authStore.organization?.bodyMessage || <div></div>}
          </div>
        </div>
      )}
      <form
        data-testid="login-form"
        onSubmit={handleSignInClick}
        className="self-stretch flex flex-col items-start justify-start gap-[20px]">
        {orgFields.map((field, index) => (
          <LoginInput
            id={field.id}
            inputTitleText={field.title}
            inputPlaceholderText={field.placeholder}
            inputType={field.type}
            required={true}
            key={index}
          />
        ))}
        {loginErrors.length > 0 && (
          <div className="self-stretch flex flex-col items-start justify-start gap-[20px]">
            <div
              className="self-stretch relative leading-[24px] p-4 text-sm bg-lightred-100 rounded-lg"
              role="alert">
              {loginErrors.map((error, index) => (
                <div key={index}>{error}</div>
              ))}
            </div>
          </div>
        )}
        <div className="self-stretch flex flex-col items-start justify-start gap-[20px]">
          <BaseButton
            data-testid="login-button"
            disabled={false}
            variant="primary"
            className="self-stretch"
            type="submit">
            Sign in
          </BaseButton>
        </div>
      </form>
    </div>
  )
}

export default LoginForm
